import React, { useLayoutEffect, useRef, useState } from 'react';
import { TabProps, TabItem } from '../../../types/DesignSystem/Tab';
import { FOCUS_RING, FOCUS_RING_EXTENT_PX } from '../focusRing';
import { MOTION } from '../motion';
import { handleListNavKey } from '../listKeyboardNav';
import Badge from '../Badge/Badge';
import { withClassName } from '../cn';
import { splitAria } from '../ariaProps';

/**
 * § 395 — the classes a tab row takes once it overflows its column: a scroller whose padding is
 * the focus ring's reach (FOCUS_RING_EXTENT_PX), taken back at the start and the top. Literal,
 * not built from the constant: Tailwind only emits the classes it can read in the source.
 * `tabOverflow.test.tsx` pins the literals to the constant.
 */
export const OVERFLOW_ROW = 'tw-overflow-x-auto tw-overflow-y-hidden -tw-ms-[4px] -tw-mt-[4px] tw-p-[4px] tw-scroll-px-[4px]';

const Tab = React.forwardRef<HTMLDivElement, TabProps>(function Tab({
  tabs,
  activeTabId,
  defaultActiveTabId,
  onChange,
  className = '',
  listClassName = '',
  panelId,
  showIcons = true,
  'data-testid': dataTestId,
  ...restProps
}, forwardedRef) {
  /* § 384 — the primitive contract: the ref and native attributes reach the root, `aria-*`
     the tablist (`ariaProps.ts`). */
  const { aria, rest } = splitAria(restProps);
  /* § 357 — `defaultActiveTabId` seeds the tab an uncontrolled Tab starts on; without it,
     the first tab, as always. */
  const [internalActiveTabId, setInternalActiveTabId] = useState<string | number>(
    activeTabId || (defaultActiveTabId ?? tabs[0]?.id)
  );

  const currentActiveTabId = activeTabId !== undefined ? activeTabId : internalActiveTabId;

  /* § 395 — the tablist is also the row that scrolls when the tabs outgrow their column (see
     the render), so one ref serves the keys, the measurement and the scrolling. */
  const tablistRef = useRef<HTMLDivElement>(null);
  /*
    § 395 — which mode the row is in. While the tabs FIT, the row is exactly the plain flex
    line it always was; only a row that overflows becomes a scroller (OVERFLOW_ROW, below).
    The ref mirrors the state so a re-check inside one commit cannot flip it twice.

    The comparison carries the mode's own geometry: an overflowing row has the ring room as
    padding on both ends and 4px more box at its start, so it would fit as a plain row
    exactly when its content is at most `clientWidth + FOCUS_RING_EXTENT_PX` wide. That is
    also what keeps the two modes from chasing each other around the boundary.
  */
  const [overflowing, setOverflowing] = useState(false);
  const overflowingRef = useRef(false);
  const syncOverflow = () => {
    const scroller = tablistRef.current;
    if (!scroller) return;
    const slack = overflowingRef.current ? FOCUS_RING_EXTENT_PX : 0;
    const next = scroller.scrollWidth > scroller.clientWidth + slack;
    if (next !== overflowingRef.current) {
      overflowingRef.current = next;
      setOverflowing(next);
    }
  };
  /* After EVERY commit, not on a dependency list: a label or a badge count can widen the tabs
     in a commit that changes neither the selection nor the tab count. What arrives WITHOUT a
     commit — a narrower column, a late webfont — is the observer's (below), which re-checks
     too. Reading two widths is the whole cost, and a same-value set is a no-op. */
  useLayoutEffect(syncOverflow);

  /*
    § 302 — the tab ↔ panel association the APG pattern requires, and this component
    never made: tabs carried `role="tab"` and `aria-selected`, the panel `role="tabpanel"`,
    and nothing joined them. Every tab has a DOM id now (`htmlId` when given — the
    stable spelling a spec selects — else one derived from `useId`; a logical id may
    hold spaces, so it is slugged), the SELECTED tab points `aria-controls` at the
    panel, and the panel names itself by that tab. Only the selected tab carries
    `aria-controls`: the other panels are not in the DOM, and an idref to nothing is
    worse than none. The panel is either this component's own (`tabs[].content`) or
    the host's, through `panelId` (SidePanel).
  */
  const uid = React.useId();
  const domIdOf = (tab: TabItem) => tab.htmlId ?? `${uid}-tab-${String(tab.id).replace(/\s+/g, '-')}`;
  const ownPanelId = `${uid}-panel`;

  const handleTabClick = (tabId: string | number, disabled?: boolean) => {
    if (!disabled) {
      setInternalActiveTabId(tabId);
      onChange?.(tabId);
    }
  };

  const activeTab = tabs.find((tab) => tab.id === currentActiveTabId);
  const rendersOwnPanel = Boolean(activeTab && activeTab.content);
  const controlsId = rendersOwnPanel ? ownPanelId : panelId;

  /**
   * Which tab owns the tablist's single tab stop.
   *
   * A roving tabindex REPLACES the natural tab stop every button used to have, so it
   * must produce one in every state. Keying it on `isActive` alone did not: a
   * controlled `activeTabId` matching no tab — or matching a **disabled** one — left
   * all of them at `tabIndex={-1}` and the whole tablist dropped out of the tab
   * order. Falling back to the first enabled tab keeps the APG's "exactly one tab
   * stop" true whenever any tab can take focus, and `-1` when none can, which is
   * then correct rather than accidental.
   *
   * Disabled is checked on the selected tab too, because `tabIndex={0}` on a
   * natively-disabled button is not focusable — one tab stop that cannot be reached
   * is the same defect wearing a passing assertion.
   */
  const rovingIndex = (() => {
    const selected = tabs.findIndex((tab) => tab.id === currentActiveTabId && !tab.disabled);
    return selected !== -1 ? selected : tabs.findIndex((tab) => !tab.disabled);
  })();

  /**
   * The sliding indicator's geometry, measured from the selected tab.
   *
   * **Why measuring is unavoidable.** The underline used to be a `<div>` rendered
   * *inside* the active button. Switching tabs unmounted it from one element and
   * mounted it in another, and a CSS transition animates a property on ONE element —
   * two different elements cannot tween between each other. So it teleported, and no
   * transition class placed on it could ever have helped. One indicator owned by the
   * tablist is what makes the movement expressible at all.
   *
   * `useLayoutEffect`, not `useEffect`: it runs after the DOM mutation but **before
   * paint**, so the measured position is already correct on the frame the tab
   * changes. With `useEffect` the bar would paint once at the old position and then
   * jump.
   *
   * The `ResizeObserver` covers what the dependency array cannot see — the container
   * getting narrower, and a webfont landing after first paint. Both change every
   * tab's width without changing any prop.
   *
   * § 395 — it watches every TAB as well as the list. Watching the list alone missed the
   * webfont this comment always named: the list's box is set by its column, so when Noto
   * Sans landed and every tab widened, the list did not resize and nothing re-measured.
   * Measured in Chromium after `document.fonts.ready`: the indicator 8px left of its tab
   * (591 vs 599) on the `tab-overflow-last` fixture, 1px short on `tab-fits` — and an
   * overflowing row scrolled 7px short of its selected tab. A tab's own box is what a font,
   * a label or a badge count changes, so the tabs are what is observed.
   */
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const list = tablistRef.current;
    if (!list) return undefined;

    const measure = () => {
      const active = list.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      const left = active ? active.offsetLeft : 0;
      const width = active ? active.offsetWidth : 0;
      /*
        Returning `prev` when nothing moved keeps a re-measure that found no change from
        costing a render. The `ResizeObserver` fires on every container resize, and a
        drag on a split pane produces a stream of them; a fresh `{left, width}` object
        each time is never `Object.is`-equal, so every one of those would re-render.

        This is NOT guarding an infinite loop — an earlier version of this comment
        claimed it was, and that was wrong. A component's own state update does not
        change its props, so `tabs` keeps its identity across these re-renders and the
        effect does not re-run. The loop cannot happen; the wasted renders can.
      */
      setIndicator((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));

      /*
        § 395 — the selected tab is kept in view, ring included. A row that scrolls can hold
        its selection out of sight: a `defaultActiveTabId` on the last tab, a click on a tab
        half under the edge, a column that narrowed. Only the ROW scrolls — `scrollIntoView`
        would scroll the page too, and a tab row below the fold must not drag the page to
        itself on mount. Nothing happens while the row fits (jsdom's widths are 0: it fits).
      */
      syncOverflow();
      const scroller = tablistRef.current;
      /* The DOM's mode, not the ref's: in the commit where the row has just started to
         overflow, the ref already says so but the tabs still sit at their plain-row offsets,
         and a scroll computed from those would be off by the ring room. The mode change
         re-runs this effect once the scroller's classes are on. */
      if (active && scroller && scroller.dataset.overflowing === 'true') {
        const start = active.offsetLeft - FOCUS_RING_EXTENT_PX;
        const end = active.offsetLeft + active.offsetWidth + FOCUS_RING_EXTENT_PX;
        const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        if (start < scroller.scrollLeft) scroller.scrollLeft = Math.max(0, start);
        else if (end > scroller.scrollLeft + scroller.clientWidth) scroller.scrollLeft = Math.min(max, end - scroller.clientWidth);
      }
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    list.querySelectorAll<HTMLElement>('[role="tab"]').forEach((tab) => observer.observe(tab));
    return () => observer.disconnect();
    /*
      `tabs.length`, not `tabs`. The array is nearly always an inline literal at the
      call site, so depending on it re-runs this effect — tearing down and rebuilding
      the ResizeObserver — on every render of the PARENT, which for a parent holding
      its own state is constantly. Length is what actually changes the geometry.
      Pinned below by counting ResizeObserver constructions across parent re-renders.
      `overflowing` (§ 395): a change of mode moves every tab by the ring room, so the bar
      is re-measured in the new geometry — once per change of mode, which is rare.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActiveTabId, tabs.length, overflowing]);

  // Icon container styles
  const iconContainer = 'tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6';

  /*
    § 295 — the count is a `Badge`, not a private pill. Tab drew its own 22px chip with a
    HALF-PIXEL border (which Chrome paints as 0 or 1px depending on zoom) because Badge
    was 28px and had no selection variant.
    `size="sm"` and `variant="selected"` exist now; the active count wears the § 51
    selection pair as before, the idle count takes Badge's `neutral` — the QUIET count
    (§ 208) — instead of the `tone-neutral-tint` slab under full ink, which out-shouted
    the idle tab's own `ink-tertiary` label. Weight no longer flips with selection
    (§ 49): Badge carries one token in every state.
  */

  return (
    <div {...rest} ref={forwardedRef} className={withClassName(`tw-flex tw-flex-col`, className)} data-testid={dataTestId}>
      <div
        /* `listClassName` (§ 296) lands HERE, on the row that draws the rule — see the
           type file for why a host needs this seam rather than a wrapper. § 395: this row is
           no longer the tablist itself; the tablist is the row inside it, which scrolls. */
        className={`tw-relative tw-flex tw-border-b tw-border-edge ${listClassName}`.trim()}
        data-testid={dataTestId ? `${dataTestId}-list` : undefined}
      >
        {/*
          § 395 — the tabs SCROLL when they outgrow their column. The row was a plain flex
          line, so a tab that did not fit was simply drawn past the column's edge: over the
          next column, under an overflow-hidden ancestor, or off a drawer's edge where no
          pointer could reach it, while the arrow keys still moved focus there (WCAG 2.4.11).

          The two-tone ring is why this is not one class. FOCUS_RING draws 4px OUTSIDE the
          tab, and a scroll container clips everything past its padding box, so a bare
          `overflow-x-auto` cut the ring off above, below and at both ends. And the ring room
          cannot be a permanent bleed either: a box pulled 4px past the list's end with a
          negative margin is LAYOUT, not ink, so a full-width tab row inside any scrolling
          page would have given that page a 4px horizontal scrollbar — the ring, a
          box-shadow, never did. Hence two modes:

          - **Fits** (every row today): a plain flex line with no overflow, no padding and
            no margin — the tabs, the ring and the indicator on the same pixels as before.
          - **Overflows** (`data-overflowing`, OVERFLOW_ROW): a scroller with the ring's 4px
            as padding all round, taken back on the START side and the top with negative
            margins (the directions that never scroll), so nothing jumps when the row
            switches; the END keeps its padding inside the scroll, which is the ring room
            of the last tab at full scroll. `scroll-px-[4px]` stops focus scrolling with
            the ring showing. The scrollbar is the app's own (§ 168) and sits between the
            tabs and their rule.

          The scroller IS the tablist, inside the row that draws the rule (§ 296's seam stays
          on that row). § 397: it had been a generic element inside the tablist, and the
          engine run found why that cannot work — Firefox makes any scrolling element a Tab
          stop, and the only way to stop it (`tabIndex={-1}`) makes the wrapper a FOCUSABLE
          non-tab inside a tablist, which is invalid ARIA (axe: aria-required-children). A
          focusable tablist is valid, so the tablist scrolls; the tabs are its children again.
        */}
        <div
          ref={tablistRef}
          {...aria}
          role="tablist"
          aria-orientation="horizontal"
          onKeyDown={(event) =>
            handleListNavKey(event, {
              container: tablistRef.current,
              itemSelector: '[role="tab"]',
              orientation: 'horizontal',
              // Automatic activation: arrowing selects as it moves. The ARIA APG
              // recommends this when the panel appears instantly, which it does
              // here — `content` is an already-built ReactNode supplied by the
              // caller, not something fetched on demand.
              onMove: (item) => {
                item.focus();
                const moved = tabs.find((candidate) => String(candidate.id) === item.dataset.tabId);
                if (moved) handleTabClick(moved.id, moved.disabled);
              },
            })
          }
          className={`tw-relative tw-flex tw-min-w-0 tw-flex-1 ${overflowing ? OVERFLOW_ROW : ''}`.trim()}
          data-overflowing={overflowing || undefined}
          /* § 397 — Firefox makes any element that scrolls a Tab stop, children or not, so the
             overflowing row became an unnamed stop in front of its own tabs (found by the
             WebKit + Firefox run). The tabs ARE the keyboard's way through the row — focusing
             one scrolls it into view — so the row itself is taken out of the Tab order. */
          tabIndex={overflowing ? -1 : undefined}
          data-testid={dataTestId ? `${dataTestId}-tablist` : undefined}
        >
          {tabs.map((tab: TabItem, index: number) => {
            const isActive = tab.id === currentActiveTabId;
            const isDisabled = tab.disabled;

            // Text styles based on state
            const textStyles = isDisabled
              ? 'tw-text-ink-disabled'
              : isActive
              ? 'tw-text-accent tw-font-semibold'
              // `grey-600`, not `grey-900`. At grey-900 an INACTIVE tab was 14.10:1 while
              // the active tab is 8.53:1 — the unselected tabs literally out-contrasted
              // the selected one, so selection was carried by hue alone. grey-600 is
              // 4.52:1: still AA, and now the active tab leads. `tw-font-normal` and
              // `tw-font-semibold` still decide the weight (§ 236): `body-primary`
              // declares 400, and fontWeight utilities emit AFTER fontSize in the
              // compiled sheet, so the state classes override the token — the same
              // mechanism every token+weight pairing in the system rides.
              : 'tw-text-ink-tertiary tw-font-normal hover:tw-text-accent';

            return (
              <button
                type="button"
                key={tab.id}
                id={domIdOf(tab)}
                aria-controls={isActive ? controlsId : undefined}
                onClick={() => handleTabClick(tab.id, isDisabled)}
                disabled={isDisabled}
                // Roving tabindex: the tablist is one tab stop, arrows move within it.
                tabIndex={index === rovingIndex ? 0 : -1}
                data-tab-id={tab.id}
                /* § 236 — `body-primary`, not the raw `tw-text-sm` (14px with the PAGE'S
                   inherited ~1.5 line-height — a 21px line box nobody chose). The token's
                   20px box takes the tab button 45→44px: onto the control grid's `lg`
                   rung instead of one pixel off it. `select-none` (§ 235): tabs are the
                   double-click magnet of the set. */
                className={`tw-px-3 tw-py-3 tw-text-body-primary tw-select-none tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING} ${textStyles} ${
                  isDisabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
                } ${tab.align === 'end' ? 'tw-ms-auto' : ''}`}
                role="tab"
                aria-selected={isActive}
                aria-disabled={isDisabled}
                data-testid={tab['data-testid']}
              >
                <div className="tw-flex tw-items-center tw-gap-2">
                  {/* Left Icon */}
                  {showIcons && tab.icon && (
                    <span className={iconContainer}>{tab.icon}</span>
                  )}

                  {/* Label — the bold ghost (§ 55). Active is semibold and
                      inactive normal, so without this the label CHANGED WIDTH on
                      selection, reflowed the whole row, and sent the animated
                      indicator chasing moving targets. A ::before pseudo-element
                      repeats the label in semibold at zero height, reserving the
                      bold width in every state — a PSEUDO, deliberately, not a
                      hidden DOM duplicate: pseudo text exists only in CSS, so it
                      cannot double every label for screen readers, text queries
                      or copy-paste. */}
                  {/* The marker is a SIBLING of the label span, not a child: that span
                      is `flex-col` (it stacks the bold ghost above the text), so an
                      asterisk inside it would land on its own line. `gap-0.5` keeps it
                      against the word — the `rightIcon` slot below is 24px of icon box
                      after the row's own 8px gap, which is much too far for a marker. */}
                  <span className="tw-flex tw-items-start tw-gap-0.5">
                    <span
                      data-label={tab.label}
                      className="tw-flex tw-flex-col tw-whitespace-nowrap before:tw-content-[attr(data-label)] before:tw-font-semibold before:tw-invisible before:tw-h-0 before:tw-overflow-hidden"
                    >
                      {tab.label}
                    </span>
                    {tab.required && (
                      <span aria-hidden="true" className="tw-text-tone-danger-ink">*</span>
                    )}
                  </span>

                  {/* Right Icon */}
                  {showIcons && tab.rightIcon && (
                    <span className={iconContainer}>{tab.rightIcon}</span>
                  )}

                  {/* Badge — see the § 295 note above. */}
                  {tab.badge !== undefined && (
                    <Badge size="sm" variant={isActive ? 'selected' : 'neutral'}>
                      {tab.badge}
                    </Badge>
                  )}
                </div>

              </button>
            );
          })}

          {/*
            One indicator for the whole tablist, so switching tabs MOVES it instead of
            destroying it and building another one somewhere else.

            `aria-hidden`: selection is already announced by `aria-selected` on the tab.
            This bar is the visual half of the same fact, and a screen reader should not
            meet it twice.

            It stays mounted at `scaleX(0)` rather than being conditionally rendered, so
            the element a spec looks for exists on the first render. In a browser that
            zero is never painted — `useLayoutEffect` measures before paint. In jsdom,
            which has no layout, it stays zero and the tests below drive `offsetLeft`
            and `offsetWidth` themselves.
          */}
          <span
            aria-hidden="true"
            data-testid={dataTestId ? `${dataTestId}-indicator` : undefined}
            /* `forced-colors:tw-bg-[color:Highlight]` (§ 224): Windows High Contrast
               strips author backgrounds, which erased the selection bar entirely —
               the system Highlight colour is the one background that mode honours,
               and it is that palette's own word for "selected". */
            /* § 297 — 2px, on the spacing scale. At 3px the bar read as a bumper under the
               label rather than a rule beside the tablist's own 1px baseline; 2px is the
               weight Linear, Vercel and the current ADS tabs all settle on, and it is the
               first rung of the scale rather than an arbitrary value. */
            /* § 395 — it lives in the tabs' row, so it scrolls WITH them. On the tabs' bottom
               edge in both modes: the row's own bottom while it fits, the ring room's inner
               edge (`bottom-[4px]`) once it scrolls. */
            className={`tw-absolute ${overflowing ? 'tw-bottom-[4px]' : 'tw-bottom-0'} tw-left-0 tw-h-0.5 tw-w-px tw-origin-left tw-bg-accent forced-colors:tw-bg-[color:Highlight] ${MOTION.tab.indicator}`}
            style={{ transform: `translateX(${indicator.left}px) scaleX(${indicator.width})` }}
          />
        </div>
      </div>

      {activeTab && activeTab.content && (
        /* § 302 — named by its tab, and a TAB STOP (`tabIndex={0}`): the APG asks for
           one so a keyboard user arriving from the tablist lands ON the panel rather
           than past it, and the panel's own ring (FOCUS_RING) shows where they are. */
        <div
          className={`tw-mt-4 tw-p-4 tw-bg-surface tw-text-ink tw-rounded-srf tw-border tw-border-edge ${FOCUS_RING}`}
          role="tabpanel"
          id={ownPanelId}
          aria-labelledby={domIdOf(activeTab)}
          tabIndex={0}
        >
          {activeTab.content}
        </div>
      )}
    </div>
  );
});

export default Tab;
